import React from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import { Input, Select } from 'components/FormInputs';
import moment from 'logic/moment';
import { useFormik } from 'components/shared/Formik';
import DialogTemplate from 'components/shared/DialogTemplate';

const style = makeStyles((theme) => ({
  title: {
    padding: theme.spacing(2),

    '& h2': {
      textAlign: 'right',
    },
  },
  content: {
    minHeight: '20vh',
  },
}));

const AskReviewDialog = (props) => {
  const { open, handleClose, onSubmit, cottages } = props;
  const intl = useIntl();
  const classes = style();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('xs'));
  const formik = useFormik({
    initialValues: {
      text: '',
      cottageId: cottages.length === 1 ? cottages[0].id : '',
      bookingId:
        cottages.length === 1 && cottages[0].Bookings && cottages[0].Bookings.length
          ? cottages[0].Bookings[0].id
          : null,
    },
    validate: (values) => {
      const errors = {};

      if (!values.text) {
        errors.text = intl.formatMessage({
          id: 'Required',
          defaultMessage: 'Required',
        });
      }

      if (!values.cottageId) {
        errors.cottageId = intl.formatMessage({
          id: 'Required',
          defaultMessage: 'Required',
        });
      }

      return errors;
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      await onSubmit(values);
      handleClose();
    },
    validateOnBlur: true,
    validateOnChange: true,
  });
  const [selectedCottage, setSelectedCottage] = React.useState(
    cottages.length === 1 ? cottages[0] : null,
  );

  const {
    values,
    errors,
    handleSubmit,
    handleChange,
    isSubmitting,
    isValid,
    setFieldValue,
    handleBlur,
  } = formik;

  React.useEffect(() => {
    if (cottages.length === 1 || !values.cottageId) {
      return;
    }

    const cottage = cottages.find((c) => c.id === parseInt(values.cottageId, 10));

    if (cottage && cottage.Bookings && cottage.Bookings.length) {
      setSelectedCottage(cottage);
      setFieldValue('bookingId', cottage.Bookings[0].id);
    } else {
      setFieldValue('bookingId', null);
    }
  }, [values.cottageId]);

  return (
    <>
      <Dialog
        onClose={handleClose}
        open={open}
        aria-describedby="ask-review-dialog"
        maxWidth="md"
        fullScreen={isSmall}
        fullWidth
      >
        <DialogTemplate
          title={
            <FormattedMessage id="conversation.askreview.save" defaultMessage="Ask for review" />
          }
          onClose={handleClose}
        >
          <DialogContent className={classes.content}>
            <form onSubmit={handleSubmit} noValidate>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  {cottages.length > 1 && (
                    <Select
                      label={<FormattedMessage id="Cottage" defaultMessage="Cottage" />}
                      name="cottageId"
                      value={values.cottageId}
                      errors={errors}
                      options={cottages.map((c) => ({ label: c.title_sv, value: c.id }))}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      help={
                        <FormattedMessage
                          id="conversation.askreview.selectcottage"
                          defaultMessage="Select the ad where you want to ask for review"
                        />
                      }
                      required
                    />
                  )}
                  {selectedCottage &&
                    selectedCottage.Bookings &&
                    selectedCottage.Bookings.length > 1 && (
                      <Select
                        label={<FormattedMessage id="Booking" defaultMessage="Booking" />}
                        name="bookingId"
                        value={values.bookingId}
                        errors={errors}
                        options={selectedCottage.Bookings.map((booking) => ({
                          label: `#${booking.id}: ${moment(booking.check_in).format('LLL')} - ${moment(booking.check_out).format('LLL')}`,
                          value: booking.id,
                        }))}
                        onChange={handleChange}
                        help={
                          <FormattedMessage
                            id="conversation.askreview.selectbooking"
                            defaultMessage="Select the booking where you want to ask for review"
                          />
                        }
                        onBlur={handleBlur}
                      />
                    )}
                </Grid>
                <Grid item xs={12}>
                  <Input
                    label={<FormattedMessage id="Message" defaultMessage="Message" />}
                    name="text"
                    value={values.text}
                    errors={errors}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    multiline
                    rows={5}
                    variant="outlined"
                    help={
                      <FormattedMessage
                        id="conversation.askreview.message"
                        defaultMessage="Specify the message to ask the guest for a review"
                      />
                    }
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    disabled={!isValid || isSubmitting}
                    type="submit"
                    color="primary"
                    variant="contained"
                  >
                    <FormattedMessage
                      id="conversation.askreview.save"
                      defaultMessage="Ask for review"
                    />
                  </Button>
                </Grid>
              </Grid>
            </form>
          </DialogContent>
        </DialogTemplate>
      </Dialog>
    </>
  );
};

AskReviewDialog.propTypes = {
  cottages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      title_sv: PropTypes.string,
      Bookings: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number })),
    }),
  ).isRequired,
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default AskReviewDialog;
